/**
 * SessionStateManager
 * Manages the current game session ID and state on the client side.
 * Tracks state changes and communicates with the server via Socket.IO.
 */

class SessionStateManager {
  constructor() {
    this.currentSessionId = null;
    this.currentState = null;
    this.socketService = null;
  }

  /**
   * Initialize with socket service
   */
  init(socketService) {
    this.socketService = socketService;
  }

  /**
   * Set the current session ID and join the socket room
   */
  setSessionId(sessionId) {
    if (!sessionId) {
      console.warn('[SessionStateManager] Attempted to set null sessionId');
      return;
    }

    this.currentSessionId = sessionId;
    console.log(`[SessionStateManager] Session ID set to: ${sessionId}`);

    // Tell socket service to join the session room
    if (this.socketService) {
      this.socketService.joinSessionRoom(sessionId);
    }
  }

  /**
   * Update the current state and notify the server
   */
  updateState(state) {
    if (!this.currentSessionId) {
      console.warn('[SessionStateManager] Cannot update state: no session ID');
      return;
    }

    const validStates = ['join', 'pre-battle', 'battle', 'victory', 'rewards', 'battle-loot'];
    if (!validStates.includes(state)) {
      console.warn(`[SessionStateManager] Invalid state: ${state}`);
      return;
    }

    const previousState = this.currentState;
    this.currentState = state;
    console.log(`[SessionStateManager] State updated: ${previousState} -> ${state} (session: ${this.currentSessionId})`);

    // Notify server of state change
    if (this.socketService) {
      this.socketService.updateSessionState(this.currentSessionId, state);
    }

    // Dispatch custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('session-state-changed', {
      detail: {
        sessionId: this.currentSessionId,
        state: state,
        previousState: previousState
      }
    }));
  }

  /**
   * Get the current state
   */
  getState() {
    return this.currentState;
  }

  /**
   * Get the current session ID
   */
  getSessionId() {
    return this.currentSessionId;
  }

  /**
   * Reset state (e.g., when session ends)
   */
  reset() {
    this.currentSessionId = null;
    this.currentState = null;
    console.log('[SessionStateManager] State reset');
  }
}

// Export singleton instance
const sessionStateManager = new SessionStateManager();
export default sessionStateManager;

